import { ButtonSettings } from "@redotech/order-tracking-common/tracking-page-definition";
import { ProductImageAspectRatio } from "@redotech/redo-content-builder-common/image";
import {
  DiscountSettings,
  DiscountSettingsType,
} from "@redotech/redo-discounts-common/discount/discount-db-parser";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoRecommendedProduct } from "@redotech/redo-product-common/redo-recommended-product-schema";
import { upsellButtonDefaultText } from "@redotech/redo-team-common/team";
import classNames from "classnames";
import { memo, useContext, useEffect, useState } from "react";
import { RedoButton } from "../../../arbiter-components/buttons/redo-button";
import { RedoSingleSelectDropdownInput } from "../../../arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { CurrencyContext } from "../../../currency";
import { Flex } from "../../../flex";
import { Text } from "../../../text";
import { TrackingPageContext } from "../../context/tracking-page-context";
import {
  getBodyStyles,
  getButtonBorderRadius,
  getButtonHierarchy,
  getButtonStyles,
} from "../shared/block-styles";
import { calculateDiscountPrice } from "./discount-util";
import { getRecommendedProductPriceFormatter } from "./price-formatting";
import * as trackingCss from "./product-upsell.module.css";
import { UpsellRedirectHandler } from "./upsell-redirect";

export const RecommendedProductComponent = memo(
  function RecommendedProductComponent({
    discountSettings,
    product,
    index,
    upsellRedirect,
    imageAspectRatio,
    checkoutButtonSettings,
    dropdownSettings,
    buttonText,
    showCompareAtPrice,
    hidePrice,
  }: {
    product: RedoRecommendedProduct;
    index: number;
    discountSettings?: DiscountSettings;
    upsellRedirect?: UpsellRedirectHandler;
    imageAspectRatio?: number;
    checkoutButtonSettings?: ButtonSettings;
    dropdownSettings?: ButtonSettings;
    buttonText?: string;
    showCompareAtPrice?: boolean;
    hidePrice?: boolean;
  }) {
    const { t } = useTranslation();
    const { formatCurrency } = useContext(CurrencyContext);
    const trackingPageContext = useContext(TrackingPageContext);
    const [isHovered, setIsHovered] = useState(false);

    const bodyStyles = getBodyStyles(trackingPageContext?.pageSettings);
    const checkoutButtonStyles = getButtonStyles(
      trackingPageContext?.pageSettings?.pageButtonSettings,
      checkoutButtonSettings,
    );
    const buttonHierarchy = getButtonHierarchy(checkoutButtonSettings);
    const dropdownStyles = getButtonStyles(
      trackingPageContext?.pageSettings?.pageButtonSettings,
      dropdownSettings,
    );
    const buttonBorderRadius = getButtonBorderRadius(
      trackingPageContext?.pageSettings,
    );
    const bodyFontFamily =
      trackingPageContext?.pageSettings?.typography.bodyFontFamily;

    const getImageUrl = (product: RedoRecommendedProduct) => {
      const imageUrl =
        isHovered && product?.altImage?.url && product?.altImage?.url
          ? new URL(product?.altImage?.url)
          : product?.featuredImage?.url
            ? new URL(product?.featuredImage?.url)
            : null;
      if (!imageUrl) {
        return null;
      }

      imageUrl.searchParams.append("width", "144");
      return imageUrl.toString();
    };
    const getAltText = (product: RedoRecommendedProduct) => {
      return isHovered && product?.altImage?.altText
        ? product?.altImage?.altText
        : product?.featuredImage?.altText;
    };

    const options = product.options.reduce(
      (acc, option) => {
        // Products with no variants have a "Title" option, we want to skip these
        if (option.name === "Title") {
          return acc;
        }

        acc[option.name] = option.values;
        return acc;
      },
      {} as Record<string, string[]>,
    );

    const [selectedOptions, setSelectedOptions] = useState<
      { name: string; value: string }[] | undefined
    >(undefined);

    // select the default variant
    useEffect(() => {
      setSelectedOptions(
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        product.options.map((option) => ({
          name: option.name,
          value: option.values[0],
        })),
      );
    }, [product.options]);

    const variants = product.variants.nodes.map((variant) => variant);

    const selectedVariant = variants.find((variant) => {
      return selectedOptions?.every((selectedOption) =>
        variant.selectedOptions.some(
          (option) =>
            option.name === selectedOption.name &&
            option.value === selectedOption.value,
        ),
      );
    });

    const selectedOption = (option: string) =>
      selectedOptions?.find((selectedOption) => selectedOption.name === option);

    return (
      <div aria-label={t("Upsell product")}>
        <Flex align="center" className={trackingCss.product} key={index}>
          <img
            alt={getAltText(product) ?? undefined}
            className={trackingCss.productImage}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            src={getImageUrl(product) ?? undefined}
            style={{
              aspectRatio: imageAspectRatio ?? ProductImageAspectRatio.SQUARE,
              height: "auto",
            }}
          />
          <Flex
            className={trackingCss.productInfo}
            dir="row"
            grow={1}
            justify="space-between"
          >
            <Flex
              className={trackingCss.productDetails}
              dir="column"
              flex={1}
              gap="xs"
            >
              <Text
                className={classNames(trackingCss.productTitle)}
                fontSize="xs"
                fontWeight="thin"
                style={bodyStyles}
                textColor="secondary"
              >
                {product.title}
              </Text>
              {!hidePrice && (
                <span aria-label={t("Product price")}>
                  {getPriceDetails(
                    product,
                    selectedVariant,
                    formatCurrency,
                    discountSettings,
                    showCompareAtPrice,
                  )}
                </span>
              )}
              {variants.length > 1 &&
                Object.keys(options).map((option, index) => (
                  <Flex key={index}>
                    <RedoSingleSelectDropdownInput
                      dangerousStyleThatShouldOnlyBeUsedForMerchantBranding={{
                        ...dropdownStyles,
                        fontFamily: bodyFontFamily,
                      }}
                      fitToAnchor={false}
                      flexGrow={false}
                      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                      options={options[option].map((option) => ({
                        value: option,
                        id: option,
                        type: "text",
                        text: option,
                      }))}
                      optionSelected={({ item: selectedOption }) => {
                        setSelectedOptions((prevOptions) => {
                          const existingOption = prevOptions?.find(
                            (prevOption) => prevOption.name === option,
                          );
                          if (existingOption) {
                            return prevOptions?.map((prevOption) =>
                              prevOption.name === option
                                ? { name: option, value: selectedOption.value }
                                : prevOption,
                            );
                          }
                          return [
                            ...(prevOptions || []),
                            { name: option, value: selectedOption.value },
                          ];
                        });
                      }}
                      selectedItem={
                        selectedOption(option)
                          ? {
                              value: selectedOption(option)?.value ?? "",
                              id: selectedOption(option)?.value ?? "",
                              type: "text",
                              text: selectedOption(option)?.value ?? "",
                            }
                          : {
                              value: option,
                              id: option,
                              type: "text",
                              text: option,
                            }
                      }
                      size="xs"
                    />
                  </Flex>
                ))}
            </Flex>
            <Flex
              align="center"
              className={trackingCss.addToCartWrapper}
              justify="center"
            >
              <a
                onClick={() =>
                  upsellRedirect?.logUpsellAndRedirect({
                    id: selectedVariant?.id?.split("/").pop() ?? "",
                    handle: product.handle,
                    productUrl: product.onlineStoreUrl,
                  })
                }
                rel="noreferrer"
                target="_blank"
              >
                <RedoButton
                  dangerousStyleThatShouldOnlyBeUsedForMerchantBranding={{
                    ...checkoutButtonStyles,
                    ...buttonBorderRadius,
                  }}
                  disabled={!selectedVariant}
                  hierarchy={buttonHierarchy}
                  size="sm"
                  text={buttonText || upsellButtonDefaultText}
                  theme="normal"
                />
              </a>
            </Flex>
          </Flex>
        </Flex>
      </div>
    );
  },
);

export const getPriceDetails = (
  product: RedoRecommendedProduct,
  selectedVariant:
    | RedoRecommendedProduct["variants"]["nodes"][number]
    | undefined,
  contextFormatCurrency: (amount: number) => string,
  discountSettings?: DiscountSettings,
  showCompareAtPrice?: boolean,
) => {
  const formatCurrency = getRecommendedProductPriceFormatter({
    product,
    selectedVariant,
    contextFormatCurrency,
  });

  const basePrice = selectedVariant?.price.amount ?? product.price;

  const parsedCompareAt = selectedVariant?.compareAtPrice
    ? Number(selectedVariant.compareAtPrice)
    : undefined;
  const compareAtPrice =
    showCompareAtPrice &&
    parsedCompareAt &&
    !Number.isNaN(parsedCompareAt) &&
    parsedCompareAt > basePrice
      ? parsedCompareAt
      : undefined;

  // Only BASIC discounts support product price calculations
  if (
    !discountSettings ||
    discountSettings.settingsType !== DiscountSettingsType.BASIC
  ) {
    return (
      <span>
        {compareAtPrice && (
          <span className={classNames(trackingCss.strikethrough)}>
            {formatCurrency(compareAtPrice)}
          </span>
        )}
        <Text fontWeight="semibold">{formatCurrency(basePrice)}</Text>
      </span>
    );
  }

  const { price, discounted } = calculateDiscountPrice(
    product,
    basePrice,
    discountSettings,
  );
  // When compareAt is available, prefer it as the struck-through "original"
  // reference price (it's the larger customer-facing amount). Otherwise fall
  // back to the basePrice strikethrough that signals the discount.
  const strikethroughAmount =
    compareAtPrice ?? (discounted ? basePrice : undefined);
  return (
    <span>
      {strikethroughAmount !== undefined && (
        <span className={classNames(trackingCss.strikethrough)}>
          {formatCurrency(strikethroughAmount)}
        </span>
      )}
      <Text fontWeight="semibold">{formatCurrency(price)}</Text>
    </span>
  );
};
